<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useCampaignStore } from '@/stores';
import { useRouter } from 'vue-router';
import { closeModal, updateShow } from '@/app/components/modal/modals';
import type { ModalProps } from '@/app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { DATA_QA } from './set-campaign-draft.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import {
  CampaignPatchActionEnum,
  CampaignStateEnum,
} from '@/apis/generated/dialbunny';
import logger from '@/logger';

const log = logger.getChild('SetCampaignToDraft');
log.debug('Mounting');

const router = useRouter();

const props = defineProps<ModalProps>();

if (!props.meta?.id) {
  log.error('Missing campaign ID in modal meta');
  void closeModal();
}

const mounted = ref(false);

onMounted(() => {
  log.debug('Mounted');
  mounted.value = true;
});

const show = computed(() => mounted.value && !!campaign.value.item);

const campaignStore = useCampaignStore();
const campaign = campaignStore.watch(
  () => props.meta?.id ?? '',
  'SetCampaignToDraft',
);

const settingAsDraft = ref(false);

watch(
  () => campaign.value.error,
  (error) => {
    if (error) {
      log.warn(
        `Error fetching campaign. Status: ${error.response.status}, Message: ${error.message}`,
      );
      void closeModal();
      // show toast
    }
  },
);

watch(
  [() => campaign.value.item, () => campaign.value.loading],
  ([campaignToSetAsDraft, loading]) => {
    if (!loading && !campaignToSetAsDraft) {
      log.log('Campaign was deleted');
      void closeModal();
      // show toast
    }
  },
);

watch(
  () => campaign.value.item?.state,
  (state) => {
    if (state !== CampaignStateEnum.Scheduled) {
      log.log('Campaign is not in scheduled state anymore');
      void closeModal();
      // show toast
    }
  },
);

const confirm = async () => {
  if (!campaign.value.item?.id) {
    log.error('Campaign ID is missing');
    return;
  }
  log.debug('Setting campaign as draft, id:', campaign.value.item.id);
  settingAsDraft.value = true;
  campaignStore.update(
    campaign.value.item.id,
    {
      action: CampaignPatchActionEnum.Reset,
    },
    {
      onSuccess: async (updated) => {
        // show toast

        log.debug('Campaign setted as draft');
        await router.push({
          name: 'campaign',
          params: { id: updated.id },
        });
        settingAsDraft.value = false;
      },
      onRetry: () => {
        // show toast
      },
      onError: (error) => {
        log.log('Error setting campaign as draft', error);
        settingAsDraft.value = false;
        // show toast
      },
    },
  );
};

const cancel = () => {
  void closeModal();
};
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('SET_CAMPAIGN_DRAFT_MODAL_TITLE')"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    {{ $t('SET_CAMPAIGN_DRAFT_DESCRIPTION') }}
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton :data-qa="DATA_QA.CANCEL" importance="clear" @click="cancel">
        {{ $t('SET_CAMPAIGN_DRAFT_CANCEL') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CONFIRM"
        importance="primary"
        class="d-ml6"
        :loading="settingAsDraft"
        @click="confirm"
      >
        {{ $t('SET_CAMPAIGN_DRAFT_CONFIRM') }}
      </DtButton>
    </div>
  </DtModal>
</template>
